import { ColDef, GridApi } from 'ag-grid-community';
import DataTable from 'components/tables/DataTable';
import PredefinedDataActions from 'components/tables/data/predefined/PredefinedDataActions';
import DeviceTypeColumn from 'components/tables/data/predefined/columns/DeviceTypeColumn';
import KeyColumn from 'components/tables/data/predefined/columns/KeyColumn';
import PredefinedDataIdColumn from 'components/tables/data/predefined/columns/PredefinedDataIdColumn';
import PredefinedDataTypeColumn from 'components/tables/data/predefined/columns/PredefinedDataTypeColumn';
import ValueColumn from 'components/tables/data/predefined/columns/ValueColumn';
import { PredefinedDataRow } from 'components/tables/data/predefined/types';
import DeviceTypeSelectFilter from 'components/tables/filters/DeviceTypeSelectFilter';
import {
  GlobalPredefinedDataTypeSelectFilter,
  PersonalPredefinedDataTypeSelectFilter,
} from 'components/tables/filters/PredefinedDataTypeSelectFilter';
import { useCallback, useMemo } from 'react';
import { SweetPath } from 'sweet-path';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';

/**
 * Parameters of the PredefinedDataTable component.
 */
export interface PreDefinedDataTableProps<TData> {
  action: () => Promise<TData[]>;
  addPath: string;
  editPath: SweetPath<string>;
  tableType: 'user' | 'global';
}

/**
 * Table which displays users.
 */
export default function PredefinedDataTable<
  TData extends { deviceType: { deviceTypeName: string; slug: string }; predefinedDataType: { name: string } }
>({ action, addPath, editPath, tableType }: PreDefinedDataTableProps<TData>) {
  const { tableActionMode } = useResponsiveDesign();

  /**
   * Column for the actions.
   */
  const ActionsColumn = useMemo(
    () =>
      function ActionsColumn({ api, data }: { api: GridApi; data: PredefinedDataRow }) {
        const predefinedDataId = tableType === 'user' ? data.usersPredefinedDataId : data.globalPredefinedDataId;

        return (
          <PredefinedDataActions
            predefinedDataId={predefinedDataId as number}
            predefinedDataName={data.key}
            editPath={editPath}
            tableType={tableType}
            compactOnTablet
            onDelete={() => api.applyTransaction({ remove: [data] })}
          />
        );
      },
    [editPath, tableType]
  );

  /**
   * The columns of the table.
   */
  const columns: ColDef[] = useMemo(
    () => [
      {
        field: 'globalPredefinedDataId',
        headerName: 'ID',
        sortable: true,
        unSortIcon: true,
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        resizable: false,
        hide: tableType === 'user',
        cellRenderer: tableType === 'global' ? PredefinedDataIdColumn : undefined,
      },
      {
        field: 'usersPredefinedDataId',
        headerName: 'ID',
        sortable: true,
        unSortIcon: true,
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        resizable: false,
        hide: tableType === 'global',
        cellRenderer: tableType === 'user' ? PredefinedDataIdColumn : undefined,
      },
      {
        field: 'deviceTypeName',
        headerName: 'Názov typu zariadenia',
        filter: true,
        hide: true,
      },
      {
        field: 'deviceTypeSlug',
        headerName: '',
        filter: true,
        hide: true,
      },
      {
        field: '_deviceType.deviceTypeId',
        headerName: 'ID typu zariadenia',
        hide: true,
      },
      {
        field: 'deviceType.deviceTypeId',
        headerName: 'Typ zariadenia',
        minWidth: 220,
        sortable: true,
        unSortIcon: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: DeviceTypeSelectFilter,
        cellRenderer: DeviceTypeColumn,
      },
      {
        field: 'predefinedDataTypeName',
        headerName: 'Typ údaju',
        filter: true,
        hide: true,
      },
      {
        field: 'predefinedDataType.predefinedDataTypeId',
        headerName: 'Typ údaju',
        minWidth: 275,
        sortable: true,
        unSortIcon: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent:
          tableType === 'user' ? PersonalPredefinedDataTypeSelectFilter : GlobalPredefinedDataTypeSelectFilter,
        cellRenderer: PredefinedDataTypeColumn,
      },
      {
        field: 'key',
        headerName: 'Názov',
        minWidth: 450,
        sortable: true,
        unSortIcon: true,
        cellRenderer: KeyColumn,
      },
      {
        field: 'value',
        headerName: 'Popis',
        unSortIcon: true,
        minWidth: 300,
        cellRenderer: ValueColumn,
      },
      {
        field: '_actions',
        headerName: '',
        pinned: 'right',
        width: getResponsiveActionsColumnWidth(194, tableActionMode),
        maxWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        minWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        cellRenderer: ActionsColumn,
      },
    ],
    [ActionsColumn, tableActionMode, tableType]
  );

  const tableAction = useCallback(async () => {
    const data = await action();

    return data.map((item) => ({
      ...item,
      _deviceType: item.deviceType,
      deviceTypeName: item.deviceType.deviceTypeName,
      deviceTypeSlug: item.deviceType.slug,
      predefinedDataTypeName: item.predefinedDataType.name,
    }));
  }, [action]);

  const columnKeys = [];

  if (tableType === 'user') {
    columnKeys.push('usersPredefinedDataId');
  } else if (tableType === 'global') {
    columnKeys.push('globalPredefinedDataId');
  }

  columnKeys.push('predefinedDataTypeName', 'key', 'value', 'deviceTypeName', '_deviceType.deviceTypeId');

  return (
    <DataTable
      tableId={`predefined-data-${tableType}`}
      title={tableType === 'user' ? 'Predvolené údaje' : 'Globálne predvolené údaje'}
      hideToggleDiscarded
      columns={columns}
      action={tableAction}
      addButtonText="Pridať nový"
      addButtonTarget={addPath}
      dataExport={{
        modalTitle: tableType === 'user' ? 'Exportovať predvolené údaje' : 'Exportovať globálne predvolené údaje',
        fileName: tableType === 'user' ? 'predvolene-udaje.xlsx' : 'globalne-predvolene-udaje.xlsx',
        columnReplacements: {
          'deviceType.deviceTypeId': ['deviceTypeName', '_deviceType.deviceTypeId'],
          'predefinedDataType.predefinedDataTypeId': ['predefinedDataTypeName'],
        },
        columnKeys,
      }}
    />
  );
}
